<template>
  <div :class="$style.wrapper">
    <label
      v-if="props.label"
      for=""
      :class="[$style.label, { [$style.disabled]: props.isDisabled }]"
    >{{ props.label }}</label>
    <div
      :class="[
        $style.inputWrapper,
        {
          [$style.disabled]: props.isDisabled,
        },
      ]"
    >
      <input
        v-model="value"
        ref="input"
        :placeholder="props.placeholder"
        :disabled="props.isDisabled"
        :readonly="props.isReadonly"
        :type="isShowPassword ? 'text' : props.type"
        :class="[
          $style.input,
          {
            [$style.withEyeIcon]: props.isWithEyeIcon,
            [$style.error]: !!props.error,
          },
        ]"
        @blur="$emit('blur')"
        @focus="$emit('focus')"
        @paste="$emit('paste')"
        @keydown="$emit('keydown')"
      />
      <div
        v-if="props.isWithEyeIcon"
        :class="$style.iconWrapper"
        @click="isShowPassword = !isShowPassword"
      >
        <CloseEyeIcon
          v-show="!isShowPassword"
          :class="$style.icon"
        />
        <EyeIcon
          v-show="isShowPassword"
          :class="$style.icon"
        />
      </div>
      <span
        v-show="props.error"
        :class="[
          $style.inputState,
          {
            [$style.error]: !!props.error,
          },
        ]"
      >{{ props.error }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

import { EyeIcon, CloseEyeIcon } from '@/shared/constants/newDesignIcons';

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'text',
  },
  isWithEyeIcon: {
    type: Boolean,
    default: false,
  },
});

const value = defineModel({ default: '', type: String });

const input = ref(null);

defineExpose({
  input,
});

const isShowPassword = ref(false);

defineEmits(['blur', 'focus', 'paste', 'keydown']);
</script>

<style lang="scss" module>
.wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  .label {
    @include base-text;
    color: $input-label-default;

    &.disabled {
      color: $input-label-disabled;
    }
  }

  .inputWrapper {
    position: relative;
    display: flex;
    width: 100%;
    align-items: center;

    &.disabled {
      pointer-events: none;
    }

    .input {
      @include base-text;
      width: 100%;
      border: 1px solid $input-default;
      padding: 12px 16px;
      border-radius: 16px;
      background-color: transparent;
      min-height: 44px;
      color: $input-text;

      @media (max-width: 992px) {
        font-size: 16px;
      }

      &::placeholder {
        color: $input-placeholder;
      }

      &:disabled {
        border-color: $input-disabled;
        background-color: $input-disabled-bg;
      }

      &:hover {
        border-color: $input-hover;
      }

      &:focus {
        border-color: $input-focus;
      }

      &.error {
        border-color: $input-error;
      }

      &.withEyeIcon {
        padding-right: 48px;
      }
    }

    .iconWrapper {
      position: absolute;
      right: 16px;
      top: 12px;
      cursor: pointer;

      .icon {
        color: $color-primary;
      }
    }
  }

  .inputState {
    @include tiny-text;
    position: absolute;
    top: 48px;

    &.error {
      color: $color-error;
    }
  }
}
</style>
